"use client";

import { useServiceAccountStatus } from "@/components/service-account-dialog";
import { useAssets, useCompleteDownload, useTrackDownload } from "@/hooks/use-assets";
import {
  AssetVariant,
  compareVersions,
  formatAssetVersionLabel,
  groupAssetsByNameVersion,
  GroupedAsset,
} from "@/lib/asset-utils";
import { toast } from "@/lib/instrumented-toast";
import { cn } from "@/lib/utils";
import { useOrgStore } from "@/stores";
import type { Asset } from "@/stores/asset-store";
import { SpanStatusCode, trace } from "@opentelemetry/api";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, DownloadIcon, InfoIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";

import { AssetMetadataDrawer } from "./asset-metadata-drawer";
import { OSIconList } from "./os-icon";
import { getOSFamilyName, getOSIconType, OSIcon } from "./os-icon";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "./ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const tracer = trace.getTracer("customer-portal-ui");

// Track expanded rows state
const useExpandedRows = () => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((id: string, firstVersion?: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      const isExpanding = !next.has(id);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      // If expanding and we have a first version, auto-expand it
      if (isExpanding && firstVersion) {
        const firstVersionId = `${id}-version-${firstVersion}`;
        setExpandedVersions((prevVersions) => {
          const nextVersions = new Set(prevVersions);
          nextVersions.add(firstVersionId);
          return nextVersions;
        });
      }

      return next;
    });
  }, []);

  const toggleVersion = useCallback((id: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback((id: string) => expandedRows.has(id), [expandedRows]);
  const isVersionExpanded = useCallback((id: string) => expandedVersions.has(id), [expandedVersions]);

  return { expandedRows, toggleRow, isExpanded, toggleVersion, isVersionExpanded };
};

// Helper component to truncate description with tooltip
const TruncatedDescription = ({ description, maxLength = 60 }: { description: string; maxLength?: number }) => {
  if (!description) return null;

  const needsTruncation = description.length > maxLength;
  const displayText = needsTruncation ? description.slice(0, maxLength) + "..." : description;

  if (!needsTruncation) {
    return <div className="truncate text-sm text-muted-foreground">{description}</div>;
  }

  return (
    <div className="flex items-center gap-1">
      <span className="truncate text-sm text-muted-foreground">{displayText}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <InfoIcon className="h-3 w-3 flex-shrink-0 cursor-help text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          <p className="whitespace-pre-wrap">{description}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

// OS/Architecture Dropdown Component for Grouped Assets
const OSVariantDropdown = ({
  groupedAsset,
  onDownload,
  onShowInstructions,
  downloadDisabled = false,
  disabledReason,
}: {
  groupedAsset: GroupedAsset;
  onDownload: (asset: Asset) => void;
  onShowInstructions: (asset: Asset) => void;
  downloadDisabled?: boolean;
  disabledReason?: string;
}) => {
  const [open, setOpen] = useState(false);

  const handleVariantClick = (variant: AssetVariant) => {
    setOpen(false);
    onDownload(variant.asset);
  };

  // Check if this is a Helm chart or Docker image
  const isHelmOrDocker =
    groupedAsset.variants[0]?.asset.asset_group === "helm charts" ||
    groupedAsset.variants[0]?.asset.asset_group === "docker images";

  // If only one variant, show Instructions button for Helm/Docker, download button for others
  if (groupedAsset.variants.length === 1) {
    const singleVariant = groupedAsset.variants[0];

    if (isHelmOrDocker) {
      return (
        <Button variant="outline" className="w-[140px] gap-1.5" onClick={() => onShowInstructions(singleVariant.asset)}>
          <InfoIcon className="h-4 w-4" />
          Instructions
        </Button>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button
              variant="outline"
              className="w-[140px] gap-1.5"
              onClick={() => onDownload(singleVariant.asset)}
              disabled={downloadDisabled}
            >
              <DownloadIcon className="h-4 w-4" />
              Download
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {downloadDisabled ? (disabledReason ?? "Downloads unavailable") : singleVariant.asset.size}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Group variants by OS family (not exact OS) for better organization
  // This groups rhel8 and rhel9 together, ubuntu22.04 and ubuntu24.04 together, etc.
  const variantsByOSFamily = groupedAsset.variants.reduce(
    (acc, variant) => {
      const detectedOS = variant.asset.detected_operating_system;
      const osFamily = getOSIconType(detectedOS);
      return {
        ...acc,
        [osFamily]: [...(acc[osFamily] || []), variant],
      };
    },
    {} as Record<string, AssetVariant[]>,
  );

  if (!isHelmOrDocker && downloadDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button variant="outline" className="w-[140px] gap-1.5" disabled>
              <DownloadIcon className="h-4 w-4" />
              Download
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason ?? "Downloads unavailable"}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {isHelmOrDocker ? (
          <Button variant="outline" className="w-[140px] gap-1.5">
            <InfoIcon className="h-4 w-4" />
            Instructions
          </Button>
        ) : (
          <Button variant="outline" className="w-[140px] gap-1.5">
            <DownloadIcon className="h-4 w-4" />
            Download
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {Object.entries(variantsByOSFamily).map(([osFamily, variants], osIndex) => (
          <div key={osFamily}>
            {osIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="flex items-center gap-2">
              <OSIcon os={variants[0].asset.detected_operating_system} size={14} />
              {getOSFamilyName(osFamily)}
            </DropdownMenuLabel>
            {variants.map((variant, idx) => {
              const detectedOS = variant.asset.detected_operating_system;
              const displayVersion = variant.asset.display_version;
              return (
                <DropdownMenuItem
                  key={idx}
                  onClick={() => (isHelmOrDocker ? onShowInstructions(variant.asset) : handleVariantClick(variant))}
                  className="pl-8"
                >
                  <div className="flex flex-col">
                    <div className="flex w-full items-center justify-between gap-4">
                      <span className="font-medium">{formatAssetVersionLabel(displayVersion)}</span>
                      {!isHelmOrDocker && <span className="text-xs text-muted-foreground">{detectedOS}</span>}
                    </div>
                    <div className="flex w-full items-center justify-between gap-4">
                      {!isHelmOrDocker && <span className="text-xs text-muted-foreground">{variant.architecture}</span>}
                      <span className="text-xs text-muted-foreground">{variant.asset.size}</span>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface AssetsDataTableProps {
  searchQuery?: string;
  selectedGroup?: string | null;
  selectedSubgroup?: string | null;
  filteredAssets?: Asset[];
  onFilteredCountChange?: (count: number, total: number) => void;
}

interface MultiSelectProps {
  title: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}

const MultiSelectFilter = ({ title, options, selected, onChange }: MultiSelectProps) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (value: string) => {
    const newSelected = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    onChange(newSelected);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-40 justify-between">
          {selected.length > 0 ? `${title} (${selected.length})` : title}
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${title.toLowerCase()}...`} />
          <CommandEmpty>No options found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {options.map((option) => (
              <CommandItem key={option} onSelect={() => toggleOption(option)}>
                <CheckIcon className={cn("mr-2 h-4 w-4", selected.includes(option) ? "opacity-100" : "opacity-0")} />
                {option}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export function DataTable({
  searchQuery = "",
  selectedGroup = null,
  selectedSubgroup = null,
  filteredAssets = undefined,
  onFilteredCountChange,
}: AssetsDataTableProps = {}) {
  const assetsQuery = useAssets();
  const allAssets = useMemo(() => assetsQuery.data?.assets ?? [], [assetsQuery.data?.assets]);
  const trackDownloadMutation = useTrackDownload();
  const completeDownloadMutation = useCompleteDownload();

  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const saQuery = useServiceAccountStatus(selectedOrgId ?? "", !!selectedOrgId);
  const noSA = saQuery.isSuccess && saQuery.data === null;
  const downloadDisabled = noSA || saQuery.isError;
  const disabledReason = noSA
    ? "No service account configured for this organization"
    : saQuery.isError
      ? "Unable to verify service account status"
      : undefined;

  const [sorting, setSorting] = useState<SortingState>([
    { id: "display_release_month", desc: true },
    { id: "asset_name", desc: false },
    { id: "display_version", desc: true },
  ]);
  const [globalFilter, setGlobalFilter] = useState<string[]>([]);
  const { toggleRow, isExpanded, toggleVersion, isVersionExpanded } = useExpandedRows();

  const [internalSearchQuery, setInternalSearchQuery] = useState(searchQuery);

  useEffect(() => {
    setInternalSearchQuery(searchQuery);
  }, [searchQuery]);

  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedArchitectures, setSelectedArchitectures] = useState<string[]>([]);
  const [selectedOS, setSelectedOS] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [metadataDrawerOpen, setMetadataDrawerOpen] = useState(false);

  const monthFilters = useMemo(() => {
    if (!allAssets || allAssets.length === 0) return [];
    return Array.from(new Set(allAssets.map((asset) => asset.display_release_month).filter(Boolean))).sort((a, b) =>
      b.localeCompare(a),
    );
  }, [allAssets]);

  const architectureFilters = useMemo(() => {
    if (!allAssets || allAssets.length === 0) return [];
    return Array.from(new Set(allAssets.map((asset) => asset.architecture).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [allAssets]);

  const osFilters = useMemo(() => {
    if (!allAssets || allAssets.length === 0) return [];
    return Array.from(new Set(allAssets.map((asset) => asset.detected_operating_system).filter(Boolean)))
      .filter((os) => os.toLowerCase() !== "linux")
      .filter((os) => os.toLowerCase() !== "kubernetes")
      .sort((a, b) => a.localeCompare(b));
  }, [allAssets]);

  const typeFilters = useMemo(() => {
    if (!allAssets || allAssets.length === 0) return [];
    return Array.from(new Set(allAssets.map((asset) => asset.asset_type).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [allAssets]);

  const handleInfoClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setMetadataDrawerOpen(true);
  };

  const handleDownload = async (asset: Asset) => {
    const span = tracer.startSpan("flow.asset_download", {
      attributes: {
        "asset.name": asset.asset_name,
        "asset.version": asset.display_version || "unknown",
        "asset.type": "file",
        "asset.group": asset.asset_group || "",
      },
    });

    const startTime = Date.now();
    let downloadId: number | null = null;

    try {
      span.addEvent("requesting_signed_url");

      await toast.promise(
        trackDownloadMutation
          .mutateAsync({
            assetPath: asset.asset_path,
            assetName: asset.asset_name,
            assetVersion: asset.display_version,
            fileSize: asset.size,
            orgId: selectedOrgId ?? undefined,
          })
          .then((response) => {
            downloadId = response.download_id;
            span.setAttribute("download.id", downloadId);
            span.addEvent("signed_url_received", { download_id: downloadId });

            if (response.url) {
              window.location.href = response.url;
              span.addEvent("download_initiated");

              const duration = Date.now() - startTime;
              if (downloadId) {
                setTimeout(() => {
                  completeDownloadMutation.mutate({ downloadId: downloadId!, durationMs: duration });
                }, 1000);
              }
            }
          }),
        {
          loading: "Generating download link...",
          success: "Download started",
          error: (err: unknown): string => {
            const detail =
              err && typeof err === "object" && "response" in err
                ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
                : undefined;
            return detail || "Failed to generate download link";
          },
        },
      );

      span.setStatus({ code: SpanStatusCode.OK });
    } catch (error) {
      span.addEvent("download_failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      span.end();
    }
  };

  // Define columns inside component to access handleInfoClick
  const columns: ColumnDef<GroupedAsset>[] = [
    {
      header: "Asset name",
      accessorKey: "asset_name",
      enableSorting: true,
      size: 500,
      minSize: 300,
      cell: ({ row }) => {
        const groupedAsset = row.original;
        const hasMultipleVariants = groupedAsset.variants.length > 1;
        const rowId = `${groupedAsset.asset_name}-${groupedAsset.display_version}`;
        const expanded = isExpanded(rowId);
        const isHelmOrDocker =
          groupedAsset.variants[0]?.asset.asset_group === "helm charts" ||
          groupedAsset.variants[0]?.asset.asset_group === "docker images";

        // Get the first version for auto-expansion
        let firstVersion: string | undefined;
        if (hasMultipleVariants) {
          const variantsByVersion = groupedAsset.variants.reduce(
            (acc, variant) => {
              const version = variant.asset.display_version;
              return {
                ...acc,
                [version]: [...(acc[version] || []), variant],
              };
            },
            {} as Record<string, AssetVariant[]>,
          );

          const sortedVersions = Object.keys(variantsByVersion).sort((a, b) => compareVersions(b, a));

          firstVersion = sortedVersions[0];
        }

        return (
          <div className="flex w-full items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {hasMultipleVariants ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 flex-shrink-0 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(rowId, firstVersion);
                  }}
                >
                  {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                </Button>
              ) : (
                <div className="h-6 w-6 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="truncate font-medium">{groupedAsset.asset_name}</div>
                <TruncatedDescription description={groupedAsset.asset_description} maxLength={60} />
              </div>
            </div>
            {!isHelmOrDocker && (
              <div className="ml-auto flex-shrink-0">
                <OSIconList
                  variants={groupedAsset.variants.map((v) => ({
                    os: v.asset.detected_operating_system,
                  }))}
                  size={16}
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "Version",
      accessorKey: "display_versions",
      enableSorting: true,
      size: 120,
      minSize: 100,
      cell: ({ row }) => {
        const groupedAsset = row.original;
        const versions = groupedAsset.display_versions;

        if (versions.length === 1) {
          return <span>{formatAssetVersionLabel(versions[0])}</span>;
        }

        // Show only the first version for multiple versions
        return <span>{formatAssetVersionLabel(versions[0])}</span>;
      },
    },
    {
      header: "Release Month",
      accessorKey: "display_release_months",
      enableSorting: true,
      size: 150,
      minSize: 120,
      cell: ({ row }) => {
        const groupedAsset = row.original;
        const months = groupedAsset.display_release_months;

        if (months.length === 1) {
          return <span>{months[0]}</span>;
        }

        // Show only the first month for multiple months
        return <span>{months[0]}</span>;
      },
    },
    {
      header: () => <div className="pr-4 text-right">Actions</div>,
      id: "actions",
      size: 200,
      minSize: 180,
      cell: ({ row }) => {
        const groupedAsset = row.original;
        const hasMultipleVariants = groupedAsset.variants.length > 1;
        const isHelmOrDocker =
          groupedAsset.variants[0]?.asset.asset_group === "helm charts" ||
          groupedAsset.variants[0]?.asset.asset_group === "docker images";

        return (
          <div className="flex items-center justify-end gap-2 pr-4">
            {!isHelmOrDocker && (
              <>
                {hasMultipleVariants ? (
                  <div className="h-9 w-9 flex-shrink-0" />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInfoClick(groupedAsset.variants[0].asset);
                    }}
                    className="h-9 w-9 flex-shrink-0 p-0"
                  >
                    <InfoIcon className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              <OSVariantDropdown
                groupedAsset={groupedAsset}
                onDownload={handleDownload}
                onShowInstructions={handleInfoClick}
                downloadDisabled={downloadDisabled}
                disabledReason={disabledReason}
              />
            </div>
          </div>
        );
      },
    },
  ];

  // Use provided filteredAssets or filter from store assets - memoized to avoid dependency issues
  const baseAssets = useMemo(() => {
    return filteredAssets !== undefined ? filteredAssets : allAssets || [];
  }, [filteredAssets, allAssets]);

  // Memoize the filtered assets computation to avoid re-filtering on every render
  const finalFilteredAssets = useMemo(() => {
    return baseAssets.filter((asset) => {
      // Apply group filtering
      if (selectedGroup) {
        // Handle the combined "docker & helm" group
        if (selectedGroup === "docker & helm") {
          if (asset.asset_group !== "docker images" && asset.asset_group !== "helm charts") return false;
        } else {
          if (asset.asset_group !== selectedGroup) return false;
        }
        if (selectedSubgroup && asset.asset_subgroup !== selectedSubgroup) return false;
      }

      // Apply multi-select filters with OR within category, AND between categories
      if (selectedMonths.length > 0 && !selectedMonths.includes(asset.display_release_month)) return false;
      if (selectedArchitectures.length > 0 && !selectedArchitectures.includes(asset.architecture)) return false;
      if (selectedOS.length > 0 && !selectedOS.includes(asset.detected_operating_system)) return false;
      if (selectedTypes.length > 0 && !selectedTypes.includes(asset.asset_type)) return false;
      if (selectedCategories.length > 0 && !selectedCategories.includes(asset.asset_category)) return false;

      // Apply search query filtering
      if (internalSearchQuery) {
        const query = internalSearchQuery.toLowerCase();
        return (
          asset.asset_name?.toLowerCase().includes(query) ||
          asset.download_name?.toLowerCase().includes(query) ||
          asset.version?.toLowerCase().includes(query) ||
          asset.asset_description?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [
    baseAssets,
    selectedGroup,
    selectedSubgroup,
    internalSearchQuery,
    selectedMonths,
    selectedArchitectures,
    selectedOS,
    selectedTypes,
    selectedCategories,
  ]);

  // Group the filtered assets by name and version
  const groupedAssets = useMemo(() => {
    return groupAssetsByNameVersion(finalFilteredAssets);
  }, [finalFilteredAssets]);

  const table = useReactTable({
    data: groupedAssets || [],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableMultiSort: true,
    state: {
      sorting,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  useEffect(() => {
    if (onFilteredCountChange) {
      const filteredCount = finalFilteredAssets.length;
      const totalCount = baseAssets.length;
      onFilteredCountChange(filteredCount, totalCount);
    }
  }, [finalFilteredAssets, baseAssets, onFilteredCountChange]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2 py-2">
        {/* Filter controls */}
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelectFilter
            title="Release Month"
            options={monthFilters}
            selected={selectedMonths}
            onChange={setSelectedMonths}
          />
          <MultiSelectFilter
            title="Architecture"
            options={architectureFilters}
            selected={selectedArchitectures}
            onChange={setSelectedArchitectures}
          />
          <MultiSelectFilter title="OS" options={osFilters} selected={selectedOS} onChange={setSelectedOS} />
          <MultiSelectFilter title="Type" options={typeFilters} selected={selectedTypes} onChange={setSelectedTypes} />

          <Button
            variant="outline"
            onClick={() => {
              setSelectedMonths([]);
              setSelectedArchitectures([]);
              setSelectedOS([]);
              setSelectedTypes([]);
              setSelectedCategories([]);
              toast.success("Filters cleared");
            }}
          >
            Clear filters
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table className="min-w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                const groupedAsset = row.original;
                const rowId = `${groupedAsset.asset_name}-${groupedAsset.display_version}`;
                const expanded = isExpanded(rowId);
                const hasMultipleVariants = groupedAsset.variants.length > 1;

                // Get the first version for auto-expansion
                let firstVersion: string | undefined;
                if (hasMultipleVariants) {
                  // Group variants by version
                  const variantsByVersion = groupedAsset.variants.reduce(
                    (acc, variant) => {
                      const version = variant.asset.display_version;
                      return {
                        ...acc,
                        [version]: [...(acc[version] || []), variant],
                      };
                    },
                    {} as Record<string, AssetVariant[]>,
                  );

                  // Sort versions descending (newest first)
                  const sortedVersions = Object.keys(variantsByVersion).sort((a, b) => compareVersions(b, a));

                  firstVersion = sortedVersions[0];
                }

                return (
                  <>
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={() => hasMultipleVariants && toggleRow(rowId, firstVersion)}
                      className={hasMultipleVariants ? "cursor-pointer hover:bg-muted/50" : ""}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                    {hasMultipleVariants &&
                      expanded &&
                      (() => {
                        // Group variants by version
                        const variantsByVersion = groupedAsset.variants.reduce(
                          (acc, variant) => {
                            const version = variant.asset.display_version;
                            return {
                              ...acc,
                              [version]: [...(acc[version] || []), variant],
                            };
                          },
                          {} as Record<string, AssetVariant[]>,
                        );

                        // Sort versions descending (newest first) using intelligent comparison
                        const sortedVersions = Object.keys(variantsByVersion).sort((a, b) => compareVersions(b, a));

                        return sortedVersions.map((version) => {
                          const variants = variantsByVersion[version];
                          const versionId = `${rowId}-version-${version}`;
                          const versionExpanded = isVersionExpanded(versionId);

                          return (
                            <React.Fragment key={`${row.id}-version-${version}`}>
                              {/* Version header row */}
                              <TableRow
                                className="cursor-pointer border-t-2 bg-muted/20 hover:bg-muted/30"
                                onClick={() => toggleVersion(versionId)}
                              >
                                <TableCell colSpan={4} className="py-2 pl-12">
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 flex-shrink-0 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleVersion(versionId);
                                      }}
                                    >
                                      {versionExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4" />
                                      ) : (
                                        <ChevronRightIcon className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <span className="text-sm font-semibold text-muted-foreground">
                                      Version {version}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      ({variants.length} {variants.length === 1 ? "variant" : "variants"})
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>

                              {/* Variant rows under this version */}
                              {versionExpanded &&
                                variants.map((variant, idx) => {
                                  const detectedOS = variant.asset.detected_operating_system;
                                  const isHelmOrDockerVariant =
                                    variant.asset.asset_group === "helm charts" ||
                                    variant.asset.asset_group === "docker images";

                                  return (
                                    <TableRow key={`${row.id}-${version}-variant-${idx}`} className="bg-muted/30">
                                      <TableCell className="pl-16">
                                        {!isHelmOrDockerVariant ? (
                                          <div className="flex items-center gap-2 text-sm">
                                            <OSIcon os={detectedOS} size={14} />
                                            <span className="text-muted-foreground">
                                              {detectedOS} {variant.architecture}
                                            </span>
                                            {variant.asset.download_name && (
                                              <span className="truncate text-xs text-muted-foreground/70">
                                                — {variant.asset.download_name}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2 text-sm">
                                            <span className="text-muted-foreground">
                                              {variant.asset.display_version}
                                            </span>
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {variant.asset.display_version}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {variant.asset.display_release_month}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center justify-end gap-2 pr-4">
                                          {variant.asset.asset_group === "helm charts" ||
                                          variant.asset.asset_group === "docker images" ? (
                                            <Button
                                              variant="outline"
                                              className="w-[140px] gap-1.5 px-3"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleInfoClick(variant.asset);
                                              }}
                                            >
                                              <InfoIcon className="h-4 w-4" />
                                              Instructions
                                            </Button>
                                          ) : (
                                            <>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleInfoClick(variant.asset);
                                                }}
                                                className="h-9 w-9 p-0"
                                              >
                                                <InfoIcon className="h-4 w-4" />
                                              </Button>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <span className="inline-block">
                                                    <Button
                                                      variant="outline"
                                                      className="w-[140px]"
                                                      disabled={downloadDisabled}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownload(variant.asset);
                                                      }}
                                                    >
                                                      Download
                                                    </Button>
                                                  </span>
                                                </TooltipTrigger>
                                                {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
                                              </Tooltip>
                                            </>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </React.Fragment>
                          );
                        });
                      })()}
                  </>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No Assets found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Metadata Drawer */}
      <AssetMetadataDrawer
        asset={selectedAsset}
        open={metadataDrawerOpen}
        onOpenChange={setMetadataDrawerOpen}
        onDownload={handleDownload}
      />
    </div>
  );
}

// Export alias for clarity
export const AssetsDataTable = DataTable;
